import { Component, computed, DestroyRef, EventEmitter, inject, Input, OnInit, Output, signal } from '@angular/core';
import { AppConfigService, AppFeature, LocalesArray } from '@markant-platform/shared/data-access';
import {
  CartridgeRole,
  DepartmentAffiliations,
  StaticRole,
  UserInviteToOrganizationDto,
} from '@markant-platform/shared/user/model';
import { catchError, EMPTY, map, Observable, of, share } from 'rxjs';
import { CartridgeWithSelectableRoles, InvitationGroup, Role } from '@markant-platform/authorizer/model';
import { OrganizationMemberInvitationSteps } from '../enums/organization-member-invitation-steps';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MarkantSelectOption } from '@markant-platform/shared/util-helpers';
import { ErrorCode } from '@markant-platform/shared/error-model';
import { AuthorizerService } from '@markant-platform/authorizer/data-access';
import { SnackbarService } from '@markant-platform/shared/snackbar/feature';
import { UserService } from '@markant-platform/shared/user/data-access';
import {
  CartridgeRoleAssignmentDataService,
  DisabledRoleLimitsStrategyService,
  OrganizationMemberRoleLimitsStrategyService,
} from '@markant-platform/shared/authorizer-utils';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { ActivatedRoute, Router } from '@angular/router';
import {
  ORGANIZATION_ROLE_CONTROL,
  USER_EXTRA_DATA_FORM,
  USER_FORM,
} from './configuration/organization-member-invitation.config';
import { HttpErrorResponse } from '@angular/common/http';
import { MarkantElementsModule } from '@markant/elements-angular';
import { OrganizationMemberInvitationAddGroupComponent } from './organization-member-invitation-add-group/organization-member-invitation-add-group.component';
import { CartridgeRoleAssignmentComponent } from '@markant-platform/shared/authorizer/feature';
import { DefaultValuePipe, FormControlErrorPipe, FormControlKeyErrorPipe } from '@markant-platform/shared/util-pipes';
import { ReactiveFormsModule } from '@angular/forms';
import { RegistrationUserComponent } from '@markant-platform/shared/registration';
import { OrganizationMemberInvitationStepHeaderComponent } from './organization-member-invitation-step-header/organization-member-invitation-step-header.component';
import { CommonModule } from '@angular/common';
import { OrganizationMemberInvitationStepperComponent } from '../organization-member-invitation-stepper/organization-member-invitation-stepper.component';
import { QualificationLevel, SalutationType } from 'data-model';

@Component({
  standalone: true,
  selector: 'markant-platform-organization-member-invitation',
  templateUrl: './organization-member-invitation.component.html',
  styleUrls: ['./organization-member-invitation.component.scss'],
  imports: [
    CommonModule,
    MarkantElementsModule,
    TranslocoPipe,
    OrganizationMemberInvitationAddGroupComponent,
    CartridgeRoleAssignmentComponent,
    DefaultValuePipe,
    FormControlKeyErrorPipe,
    ReactiveFormsModule,
    FormControlErrorPipe,
    RegistrationUserComponent,
    OrganizationMemberInvitationStepHeaderComponent,
    OrganizationMemberInvitationStepperComponent,
  ],
})
export class OrganizationMemberInvitationComponent implements OnInit {
  @Input() organizationId?: string;
  @Input() organizationQualificationLevel?: QualificationLevel | null;

  @Output() closedWithReload = new EventEmitter<boolean>();

  organizationRoleControl = ORGANIZATION_ROLE_CONTROL();
  userForm = USER_FORM();
  userExtraDataForm = USER_EXTRA_DATA_FORM();

  isLoading = false;
  isOpen = true;
  showError = false;
  currentStep: OrganizationMemberInvitationSteps = OrganizationMemberInvitationSteps.USER;
  hasOrganizationGroups = true;
  invitationGroups: InvitationGroup[] = [];
  organizationRoles$: Observable<Role[]> = of([]);
  demoRoles$: Observable<Role[]> = of([]);
  isRoleLimitEnabled = false;

  invitationGroupsSelected = signal<InvitationGroup[]>([]);
  selectedDynamicRoles = computed(() => this.cartridgeRoleAssignmentDataService.selectedRoles());

  protected roleOptions: MarkantSelectOption[] = [];
  protected readonly QualificationLevel = QualificationLevel;
  protected readonly languages = LocalesArray;
  protected readonly OrganizationMemberInvitationSteps = OrganizationMemberInvitationSteps;
  protected departmentAffiliationOptions: MarkantSelectOption[] = [];
  protected languageOptions: MarkantSelectOption[] = [];

  private readonly authorizerService = inject(AuthorizerService);
  private readonly snackBarService = inject(SnackbarService);
  private readonly userService = inject(UserService);
  private readonly translocoService = inject(TranslocoService);
  private readonly cartridgeRoleAssignmentDataService = inject(CartridgeRoleAssignmentDataService);
  private readonly disabledRoleLimitsStrategyService = inject(DisabledRoleLimitsStrategyService);
  private readonly organizationMemberRoleLimitsStrategyService = inject(OrganizationMemberRoleLimitsStrategyService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly destroyRef = inject(DestroyRef);

  ngOnInit(): void {
    this.updateSelectedOptionsData();
    this.getRolesData();

    this.isRoleLimitEnabled = AppConfigService.isFeatureEnabled(AppFeature.ROLE_LIMITS);

    const limitService = this.isRoleLimitEnabled
      ? this.organizationMemberRoleLimitsStrategyService
      : this.disabledRoleLimitsStrategyService;

    this.cartridgeRoleAssignmentDataService.init(limitService);

    this.setDefaultRoleSelection();
    this.loadInvitationGroupsData(this.organizationId);
  }

  addUser(): void {
    if (this.organizationId == null) {
      this.snackBarService.showErrorSnackbarWithTransloco('organization.users.invite.error');
      return;
    }

    this.isLoading = true;

    const user = this.getUserInviteToOrganizationDto();

    this.userService
      .inviteUser(user)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        catchError(err => {
          this.handleError(err);
          this.closeUserInviteDialog();
          this.isLoading = false;
          return EMPTY;
        })
      )
      .subscribe(({ users }) => {
        const { firstname, lastname } = user;
        const [firstUser] = users;

        this.snackBarService.showSuccessSnackbarWithTransloco('organization.users.invite.success', {
          firstname,
          lastname,
        });

        this.closeUserInviteDialog(true);

        this.isLoading = false;
        this.router.navigate([firstUser.id], { relativeTo: this.route });
      });
  }

  setDefaultRoleSelection(): void {
    if (this.organizationQualificationLevel === QualificationLevel.LEVEL_1) {
      this.demoRoles$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(demoRoles => {
        if (demoRoles?.length === 1) {
          const [firstRole] = demoRoles;
          this.updateOrganizationRoleControlStatus(firstRole.internalName as StaticRole);
        }

        this.roleOptions = this.convertToMarkantSelectionData(demoRoles);
      });
      return;
    }

    this.organizationRoles$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(organizationRoles => {
      if (organizationRoles?.length === 1) {
        const [firstRole] = organizationRoles;
        this.updateOrganizationRoleControlStatus(firstRole.internalName as StaticRole);
      }

      this.roleOptions = this.convertToMarkantSelectionData(organizationRoles);
    });
  }

  closeUserInviteDialog(reload = false): void {
    this.isOpen = false;
    this.closedWithReload.emit(reload);
  }

  proceedStep(): void {
    switch (this.currentStep) {
      case OrganizationMemberInvitationSteps.USER:
        this.toRoleView();
        break;
      case OrganizationMemberInvitationSteps.ROLE:
        this.toAssignGroupView();
        break;
      case OrganizationMemberInvitationSteps.GROUP:
        this.toAssignServicePermissionsView();
        break;
      case OrganizationMemberInvitationSteps.CARTRIDGE:
        this.addUser();
        break;
    }
  }

  backwardsStep(): void {
    switch (this.currentStep) {
      case OrganizationMemberInvitationSteps.ROLE:
        this.currentStep = OrganizationMemberInvitationSteps.USER;
        break;
      case OrganizationMemberInvitationSteps.GROUP:
        this.currentStep = OrganizationMemberInvitationSteps.ROLE;
        break;
      case OrganizationMemberInvitationSteps.CARTRIDGE:
        this.currentStep = OrganizationMemberInvitationSteps.GROUP;
        break;
    }
  }

  onGroupClick(group: InvitationGroup): void {
    const { id } = group;
    this.invitationGroupsSelected.update(invitationGroups => {
      const index = invitationGroups.findIndex(group => group.id === id);

      if (index !== -1) {
        const invitationGroupsSelected = [...invitationGroups];
        invitationGroupsSelected.splice(index, 1);

        return [...invitationGroupsSelected];
      }

      return [...invitationGroups, group];
    });
  }

  private handleError(err: HttpErrorResponse): void {
    if (err && err.status === 400 && err?.error?.errorCode === ErrorCode.USER_ALREADY_IN_ORGANIZATION) {
      this.userForm.controls.email.setErrors({ duplicate: true });
      this.snackBarService.showErrorSnackbarWithTransloco('organization.users.invite.error.USE-008');
    } else if (err?.error?.errorCode === ErrorCode.USER_INACTIVE_ORG) {
      this.snackBarService.showErrorSnackbarWithTransloco('organization.users.invite.error.USE-064');
    }
  }

  private getUserInviteToOrganizationDto(): UserInviteToOrganizationDto {
    return {
      ...this.userForm.getRawValue(),
      salutation: this.userForm.controls.salutation.value as SalutationType,
      ...this.userExtraDataForm.getRawValue(),
      organizationRole: this.organizationRoleControl.value,
      organizationId: this.organizationId ?? '',
      groupIds: this.invitationGroupsSelected().map(({ id }) => id),
      cartridgeRoles: this.generateCartridgeRoles(),
    };
  }

  private toRoleView(): void {
    if (this.userForm.invalid || this.userExtraDataForm.invalid) {
      this.showError = true;
      return;
    }

    this.showError = false;
    this.currentStep = OrganizationMemberInvitationSteps.ROLE;
  }

  private toAssignGroupView(): void {
    this.showError = false;
    this.currentStep = OrganizationMemberInvitationSteps.GROUP;
  }

  private toAssignServicePermissionsView(): void {
    this.showError = false;
    this.currentStep = OrganizationMemberInvitationSteps.CARTRIDGE;
  }

  private loadInvitationGroupsData(organizationId?: string): void {
    if (!organizationId) {
      return;
    }

    this.authorizerService
      .getInvitationGroups(organizationId)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        map(data => data.result),
        catchError(() => of([] as InvitationGroup[]))
      )
      .subscribe(data => {
        this.invitationGroups = data;

        if (!data.length) {
          this.hasOrganizationGroups = false;
        }
      });
  }

  private generateCartridgeRoles(): CartridgeRole[] {
    return this.selectedDynamicRoles().reduce((accumulator: CartridgeRole[], service: CartridgeWithSelectableRoles) => {
      return [
        ...accumulator,
        ...service.roles.map(({ id, cartridgeId, suborganizationData }) => ({
          roleId: id,
          cartridgeVariantId: cartridgeId,
          suborganizationIds: suborganizationData?.map(({ id }) => id),
        })),
      ];
    }, []);
  }

  private updateSelectedOptionsData(): void {
    this.departmentAffiliationOptions = Object.values(DepartmentAffiliations)
      .map((value: string) => ({
        value,
        representation: this.translocoService.translate(`register.user.departmentOptions.${value}`),
      }))
      .sort((a, b) => a.representation.localeCompare(b.representation));

    this.languageOptions = LocalesArray.map(({ shorthand }) => ({
      value: shorthand,
      representation: this.translocoService.translate(`languageSelect.${shorthand}`),
    }));
  }

  private updateOrganizationRoleControlStatus(staticRole: StaticRole): void {
    this.organizationRoleControl.setValue(staticRole);
    this.organizationRoleControl.disable();
  }

  private convertToMarkantSelectionData(roles: Role[]): MarkantSelectOption[] {
    return roles.map(({ internalName, localizedDisplayName }) => ({
      value: internalName,
      representation: localizedDisplayName,
    }));
  }

  private getRolesData(): void {
    this.organizationRoles$ = this.authorizerService.getAssignableOrganizationRoles(this.organizationId).pipe(
      map(roles => {
        roles.sort((a, b) => a.internalName.localeCompare(b.internalName));
        return roles;
      })
    );

    this.demoRoles$ = this.authorizerService.getAssignableDemoRoles(this.organizationId).pipe(
      map(roles => {
        roles.sort((a, b) => a.internalName.localeCompare(b.internalName));
        return roles;
      }),
      share()
    );
  }
}
